import { useMemo } from "react";
import { useLang } from "@/context/AppContext";
import { STATE_STYLE } from "@/components/StateBadge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DEFAULT_FILTERS = { states: [], sector: "all", minTrust: 0 };

export const applyFilters = (arcs, f) => arcs.filter((a) =>
  (!f.states.length || f.states.includes(a.state)) &&
  (f.sector === "all" || a.person.sector === f.sector || a.company.sector === f.sector) &&
  Math.min(a.person.trust, a.company.trust) >= f.minTrust);

export const GlobeFilters = ({ arcs, f, setF, shown }) => {
  const { t } = useLang();
  const counts = arcs.reduce((m, a) => ({ ...m, [a.state]: (m[a.state] || 0) + 1 }), {});
  const sectors = [...new Set(arcs.flatMap((a) => [a.person.sector, a.company.sector]).filter(Boolean))].sort();
  const trustValue = useMemo(() => [f.minTrust], [f.minTrust]);
  const toggle = (s) => setF({ ...f, states: f.states.includes(s) ? f.states.filter((x) => x !== s) : [...f.states, s] });
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 rounded-md border border-white/[0.08] bg-[#111827] p-4" data-testid="globe-filters">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("filters")}</span>
      <div className="flex flex-wrap gap-2" data-testid="globe-legend">
        <button onClick={() => setF({ ...f, states: [] })} data-testid="globe-filter-state-all"
          className={`rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-widest ${!f.states.length ? "border-emerald-400 text-emerald-300" : "border-white/10 text-slate-500"}`}>{t("all_states")}</button>
        {Object.entries(counts).map(([s, n]) => (
          <button key={s} onClick={() => toggle(s)} data-testid={`globe-filter-state-${s}`}
            className={`rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-widest transition-opacity ${STATE_STYLE[s]} ${f.states.length && !f.states.includes(s) ? "opacity-35" : ""}`}>
            {t(`st_${s}`)} · {n}
          </button>
        ))}
      </div>
      <Select value={f.sector} onValueChange={(v) => setF({ ...f, sector: v })}>
        <SelectTrigger className="w-48" data-testid="globe-filter-sector"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#111827]">
          <SelectItem value="all" data-testid="globe-sector-all">{t("all_sectors")}</SelectItem>
          {sectors.map((s) => <SelectItem key={s} value={s} data-testid={`globe-sector-${s}`}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex w-64 items-center gap-3">
        <span className="whitespace-nowrap text-xs text-slate-400">{t("min_trust_filter")}</span>
        <Slider value={trustValue} min={0} max={100} step={5} onValueChange={([v]) => setF({ ...f, minTrust: v })} data-testid="globe-filter-trust" />
        <span className="w-8 font-mono text-xs text-emerald-300" data-testid="globe-filter-trust-value">{f.minTrust}</span>
      </div>
      <span className="ml-auto font-mono text-[11px] text-slate-400" data-testid="globe-showing">{t("showing")} {shown}/{arcs.length}</span>
      <button onClick={() => setF(DEFAULT_FILTERS)} className="text-xs text-slate-400 hover:text-white" data-testid="globe-filter-reset">{t("reset")}</button>
    </div>
  );
};
